import React from 'react';
import { Box, Controller, costFormater, PriceInput, Text, useFormContext } from '@common-fe/common-fe';

import {
  RenewalAutoFundingConfig,
  RenewalCategorySpendLimit,
  RenewalDeductibleConfig,
} from '@/modules/Renewals/renewals.types';
import theme from '@/styles/theme';
import { formatCategoryType } from '@/utils';

import { EditAmountsFormValues } from '../EditAmountsModal.types';

interface Props {
  funding: RenewalAutoFundingConfig;
  planIndex: number;
  coverageIndex: number;
  planCsls: RenewalCategorySpendLimit[];
  originalConfig?: RenewalDeductibleConfig;
  totalPlans: number;
  testId?: string;
}

const PlanFundingCard: React.FC<Props> = ({
  funding,
  planIndex,
  coverageIndex,
  planCsls,
  originalConfig,
  totalPlans,
  testId = 'edit-amounts-modal',
}) => {
  const { control, trigger: triggerValidation, formState } = useFormContext<EditAmountsFormValues>();

  // Use originalConfigs for visibility checks (not current state which changes after save)
  const originalFunding =
    (originalConfig?.autoFundingConfigs || []).find((f) => f.planId === funding.planId) ||
    (originalConfig?.autoFundingConfigs || [])[planIndex];
  const originalCsls = (originalConfig?.categorySpendLimits || []).filter(
    (csl) => csl.planId === funding.planId
  );

  const showPerPerson = originalFunding?.perPersonSpendLimitAmount != null;
  const showAutoFunding = originalFunding?.autoEnrollmentAmount != null;
  const showCategorySpendLimits = originalCsls.length > 0;

  const { minElectionAmount, maxElectionAmount } = funding;
  const autoFundingHint =
    minElectionAmount != null && maxElectionAmount != null
      ? `According to the chosen plan, the amount must be between ${costFormater(
          minElectionAmount,
          true
        )} and ${costFormater(maxElectionAmount, true)}`
      : undefined;

  return (
    <Box
      pad="spacing16"
      border={{ size: 'small', color: 'border1' }}
      round="container2Round"
      background="canvas"
      margin={{ bottom: planIndex < totalPlans - 1 ? 'spacing12' : undefined }}
    >
      <Text size="large" weight={700} color="textBody" margin={{ bottom: 'spacing12' }}>
        {funding.planName}
      </Text>

      {/* Spend limits */}
      {(showPerPerson || showCategorySpendLimits) && (
        <>
          <Text weight={700} color="textBody" margin={{ bottom: 'spacing8' }}>
            Spend limits:
          </Text>
          <Box
            direction="row"
            wrap
            align="start"
            style={{
              columnGap: theme.spacings.spacing12,
              rowGap: theme.spacings.spacing12,
            }}
            margin={{ bottom: 'spacing12' }}
          >
            {showPerPerson && (
              <Box width={`calc(50% - ${theme.spacings.spacing6})`}>
                <Text color="textBody" margin={{ bottom: 'spacing4' }}>
                  Per person
                </Text>
                <Controller
                  name={`coverageLevels.${coverageIndex}.plans.${planIndex}.perPersonSpendLimit`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <PriceInput
                      testId={`${testId}_coverage-${coverageIndex}_plan-${planIndex}_per-person`}
                      name={field.name}
                      value={field.value}
                      onChange={(value: string) => {
                        field.onChange(value);
                        if (formState.isSubmitted) {
                          triggerValidation();
                        }
                      }}
                      placeholder="Enter $ amount"
                      errorText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
            )}
            {showCategorySpendLimits &&
              planCsls.map((csl, cslIndex) => (
                <Box key={csl.categoryType || cslIndex} width={`calc(50% - ${theme.spacings.spacing6})`}>
                  <Text color="textBody" margin={{ bottom: 'spacing4' }}>
                    {formatCategoryType(csl.categoryType || '')}
                  </Text>
                  <Controller
                    name={`coverageLevels.${coverageIndex}.plans.${planIndex}.categorySpendLimits.${cslIndex}.amount`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <PriceInput
                        testId={`${testId}_coverage-${coverageIndex}_plan-${planIndex}_category-${csl.categoryType}`}
                        name={field.name}
                        value={field.value}
                        onChange={(value: string) => {
                          field.onChange(value);
                          if (formState.isSubmitted) {
                            triggerValidation();
                          }
                        }}
                        placeholder="Enter $ amount"
                        errorText={fieldState.error?.message}
                      />
                    )}
                  />
                </Box>
              ))}
          </Box>
        </>
      )}

      {(showPerPerson || showCategorySpendLimits) && showAutoFunding && (
        <Box height="1px" background="border1" margin={{ bottom: 'spacing12' }} />
      )}

      {/* Auto-funding amount */}
      {showAutoFunding && (
        <>
          <Text weight={700} color="textBody" margin={{ bottom: 'spacing4' }}>
            Auto-funding amount:
          </Text>
          {autoFundingHint && (
            <Text color="textBody" margin={{ bottom: 'spacing8' }}>
              {autoFundingHint}
            </Text>
          )}
          <Box width={`calc(50% - ${theme.spacings.spacing6})`}>
            <Controller
              name={`coverageLevels.${coverageIndex}.plans.${planIndex}.autoFundingAmount`}
              control={control}
              render={({ field, fieldState }) => (
                <PriceInput
                  testId={`${testId}_coverage-${coverageIndex}_plan-${planIndex}_auto-funding`}
                  name={field.name}
                  value={field.value || ''}
                  onChange={(value: string) => {
                    field.onChange(value);
                    if (formState.isSubmitted) {
                      triggerValidation();
                    }
                  }}
                  placeholder="Enter $ amount"
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default PlanFundingCard;
